import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { FileSort, SortDirection, SortKey } from './types';

const STORAGE_KEY = 'product-x-next-files-sort';

export const DEFAULT_FILE_SORT: FileSort = { key: 'name', direction: 'asc' };

const SORT_KEYS: SortKey[] = ['name', 'modified_at', 'kind', 'size'];
const SORT_DIRECTIONS: SortDirection[] = ['asc', 'desc'];

export interface FileSortState {
  sort: FileSort;
  setSort: (sort: FileSort) => void;
}

function isFileSort(value: unknown): value is FileSort {
  if (typeof value !== 'object' || value === null) return false;
  const { key, direction } = value as Partial<FileSort>;
  return (
    SORT_KEYS.includes(key as SortKey) &&
    SORT_DIRECTIONS.includes(direction as SortDirection)
  );
}

/**
 * Takes the stored sort only when it is still a sort.
 *
 * Local storage holds user-editable text that outlives the shape it was
 * written in — a preference stored by an older build, or edited by hand, must
 * degrade to the default rather than sorting by a column that no longer
 * exists. Same reasoning as the route's `.catch()` per search param.
 */
export function mergeStoredSort(
  persisted: unknown,
  current: FileSortState,
): FileSortState {
  const stored = (persisted as { sort?: unknown } | undefined)?.sort;
  return { ...current, sort: isFileSort(stored) ? stored : DEFAULT_FILE_SORT };
}

/**
 * How this device orders a file listing.
 *
 * A device preference rather than route state: sorting is a view preference,
 * not a resource anybody would link to (research.md R10), so it stays out of
 * the address — but it should still be there after a reload, the same way the
 * theme choice is. Persisted through the same `zustand/persist` mechanism as
 * `useThemeStore`, and shared by every folder: nobody chooses "by size" and
 * means "only in this one folder".
 */
export const useFileSortStore = create<FileSortState>()(
  persist(
    (set) => ({
      sort: DEFAULT_FILE_SORT,
      setSort: (sort) => {
        set({ sort });
      },
    }),
    { name: STORAGE_KEY, merge: mergeStoredSort },
  ),
);
